import { Component, computed, inject, input } from '@angular/core';
import { ListingVendor } from '../../../core/models/listing-vendor';
import { NgClass, NgOptimizedImage, UpperCasePipe } from '@angular/common';
import { CantonPipe } from '../../../core/pipes/canton.pipe';
import { ToastService } from '../../../core/services/toast.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LocalizedUrlService } from '../../../core/services/localized-url.service';

@Component({
  selector: 'app-vendor-card',
  imports: [
    NgClass,
    NgOptimizedImage,
    CantonPipe,
    UpperCasePipe,
    TranslocoModule,
  ],
  template: `
    <a
      class="group relative block aspect-video overflow-hidden rounded-xl md:aspect-5/7"
      [ngClass]="{ 'cursor-not-allowed!': !vendor().bookings_are_public }"
      [attr.href]="vendor().bookings_are_public ? vendorHref() : null"
      [attr.target]="vendor().bookings_are_public ? '_blank' : undefined"
      (click)="onCardClick($event)"
    >
      <!-- Image -->
      <img
        [ngSrc]="vendor()?.photo"
        fill
        sizes="100vw"
        class="rounded-xl object-cover duration-300 ease-out group-hover:origin-center group-hover:scale-110"
        alt="{{ vendor().listing_name }}"
        title="{{ vendor().listing_name }}"
        [ngClass]="{ 'group-hover:grayscale': !vendor().bookings_are_public }"
      />

      <!-- Overlay gradient pour améliorer la lisibilité du texte -->
      <div
        class="absolute inset-x-0 bottom-0 h-2/3 bg-linear-to-t from-stone-900 to-transparent"
      ></div>

      <!-- Texte animé -->
      <div class="absolute bottom-0 z-20 w-full px-4 md:px-6">
        <div class="flex flex-col transition-transform duration-300">
          <!-- Listing name, toujours visible -->
          <span
            class="block -translate-y-6 text-lg text-white transition-transform duration-300 xl:translate-y-2 xl:text-xl xl:group-hover:-translate-y-6"
          >
            <div class="text-sm font-medium">
              {{ vendor()?.canton | canton | uppercase }}
            </div>
            <div class="leading-none font-bold">{{
              vendor().listing_name
            }}</div>
          </span>
          <!-- Nombre de salles à louer : descend au départ et remonte au hover -->
          <span
            class="block -translate-y-4 font-medium text-yellow-300 transition-all duration-300 xl:translate-y-4 xl:opacity-0 xl:group-hover:-translate-y-4 xl:group-hover:opacity-100"
          >
            @if (vendor().bookings_are_public) {
              {{
                (vendor().roomsCount > 1
                  ? 'vendor-card.rooms-to-rent.plural'
                  : 'vendor-card.rooms-to-rent.singular'
                ) | transloco: { count: vendor().roomsCount }
              }}
            } @else {
              {{ 'vendor-card.private-rentals' | transloco }}
            }
          </span>
        </div>
      </div>
    </a>
  `,
  styles: ``,
})
export class VendorCardComponent {
  vendor = input.required<ListingVendor>();
  private toastService = inject(ToastService);
  private translocoService = inject(TranslocoService);
  private localizedUrlService = inject(LocalizedUrlService);

  // Lien localisé vers la page du vendor (slug traduit selon la langue active)
  vendorHref = computed(() =>
    this.localizedUrlService
      .link('vendor', { vendorSlug: this.vendor().slug })
      .join('/'),
  );

  onCardClick(event: MouseEvent) {
    if (this.vendor().bookings_are_public) {
      return;
    }

    event.preventDefault();
    this.toastService.info(
      this.translocoService.translate('vendor-card.private-toast.message', {
        name: this.vendor().listing_name,
      }),
      this.translocoService.translate('vendor-card.private-toast.title'),
    );
  }
}
